import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    signRequestId: String,
    locale: String,
    canMutate: Boolean,
    csrfToken: String
  }

  static targets = ["list", "form", "nameInput", "emailInput"]

  connect() {
    this.signers = []
    this.loadSigners()
  }

  async loadSigners() {
    try {
      const response = await fetch(`/${this.localeValue}/sign_requests/${this.signRequestIdValue}/signers`, {
        headers: {
          "Accept": "application/json"
        }
      })

      if (response.ok) {
        this.signers = await response.json()
        this.renderSigners()
      }
    } catch (error) {
      console.error("Error loading signers:", error)
    }
  }

  async addSigner(event) {
    event.preventDefault()

    const name = this.nameInputTarget.value.trim()
    const email = this.emailInputTarget.value.trim()

    if (!name || !email) return

    try {
      const response = await fetch(`/${this.localeValue}/sign_requests/${this.signRequestIdValue}/signers`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfTokenValue
        },
        body: JSON.stringify({ signer: { name, email } })
      })

      if (response.ok) {
        const signer = await response.json()
        this.signers.push(signer)
        this.renderSigners()
        this.nameInputTarget.value = ""
        this.emailInputTarget.value = ""

        // Reload page to update prepare section visibility
        window.location.reload()
      } else {
        const error = await response.json()
        alert(error.errors.join("\n"))
      }
    } catch (error) {
      console.error("Error adding signer:", error)
    }
  }

  async removeSigner(event) {
    const signerId = event.currentTarget.dataset.signerId

    if (!confirm("Are you sure you want to remove this recipient?")) return

    try {
      const response = await fetch(`/${this.localeValue}/sign_requests/${this.signRequestIdValue}/signers/${signerId}`, {
        method: "DELETE",
        headers: {
          "X-CSRF-Token": this.csrfTokenValue
        }
      })

      if (response.ok) {
        this.signers = this.signers.filter(s => s.id !== signerId)
        this.renderSigners()

        // Reload page to update prepare section visibility
        window.location.reload()
      }
    } catch (error) {
      console.error("Error removing signer:", error)
    }
  }

  async resendEmail(event) {
    const signerId = event.currentTarget.dataset.signerId
    const signerName = event.currentTarget.dataset.signerName

    if (!confirm(`Resend signing invitation to ${signerName}? This will generate a new link and invalidate the previous one.`)) return

    const button = event.currentTarget
    button.disabled = true

    try {
      const response = await fetch(`/${this.localeValue}/sign_requests/${this.signRequestIdValue}/signers/${signerId}/resend_email`, {
        method: "POST",
        headers: {
          "X-CSRF-Token": this.csrfTokenValue
        }
      })

      if (response.ok) {
        // Reload signers to update status
        await this.loadSigners()
        // Show success feedback
        alert("Invitation resent successfully.")
      } else {
        const error = await response.json()
        alert(error.errors?.join("\n") || "Failed to resend invitation.")
      }
    } catch (error) {
      console.error("Error resending email:", error)
      alert("Failed to resend invitation.")
    } finally {
      button.disabled = false
    }
  }

  renderSigners() {
    if (!this.hasListTarget) return

    if (this.signers.length === 0) {
      this.listTarget.innerHTML = `<p class="text-muted small mb-0">No recipients added yet.</p>`
      return
    }

    this.listTarget.innerHTML = this.signers.map(signer => `
      <div class="d-flex align-items-center gap-2 mb-2 p-2 bg-light rounded">
        <span class="d-inline-block rounded-circle flex-shrink-0" style="width: 12px; height: 12px; background-color: ${signer.color_hex};"></span>
        <div class="flex-grow-1 min-width-0">
          <div class="fw-medium small text-truncate">${this.escapeHtml(signer.name)}</div>
          <div class="text-muted small text-truncate">${this.escapeHtml(signer.email)}</div>
        </div>
        <span class="badge bg-secondary">${signer.status_label}</span>
        ${signer.can_resend ? `
          <button type="button" class="btn btn-sm btn-outline-primary p-1 lh-1" data-action="click->signers#resendEmail" data-signer-id="${signer.id}" data-signer-name="${this.escapeHtml(signer.name)}" title="Resend invitation">
            <i class="bi bi-envelope-arrow-up"></i>
          </button>
        ` : ''}
        ${this.canMutateValue ? `
          <button type="button" class="btn btn-sm btn-outline-danger p-1 lh-1" data-action="click->signers#removeSigner" data-signer-id="${signer.id}">
            <i class="bi bi-trash"></i>
          </button>
        ` : ''}
      </div>
    `).join("")
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}
